import type { AxiosInstance, AxiosRequestConfig } from 'axios';
import { FINANCE, financeApi, IDENTITY, identityApi } from '@shared/api';
import type { ApplicationCode } from '@shared/config/applications';

/**
 * Where access administration requests go.
 *
 * - `finance` — the Finance application administers access through
 *   finance-service `/api/v1/access/*`, which forwards a signed call to
 *   identity-service; identity re-resolves the actor and applies every
 *   anti-escalation rule (ACCESS-ADMINISTRATION.md). Finance never touches
 *   identity_db.
 * - `identity` — every other application (HRMS) calls identity-service
 *   directly, and only for what identity allows outside FINANCE: reads,
 *   role assignment (seniority-bounded), account provisioning and password
 *   reset. Identity's global RBAC writes — role create/update/delete,
 *   role permissions, user applications, user companies — are
 *   `@ForApplications(FINANCE)`; they are simply not offered on this channel.
 *   Individual permission grants ARE sent from this channel too: identity's
 *   PUT is also FINANCE-only, so an account without the FINANCE application
 *   gets identity's own refusal rather than a button that does nothing.
 *
 * The channel is a property of the application, set once at start-up.
 */
export type AccessChannel = 'identity' | 'finance';

let current: AccessChannel = 'identity';

export function configureAccessChannel(application: ApplicationCode): void {
  current = application === 'FINANCE' ? 'finance' : 'identity';
}

export function accessChannel(): AccessChannel {
  return current;
}

export interface AccessCapabilities {
  /** Create / update roles and replace role permissions. */
  roleWrite: boolean;
  /**
   * Delete / restore roles. identity has the routes (Finance-only), but
   * finance-service exposes no `/access` equivalent — so no channel can call
   * them today (backend gap, documented).
   */
  roleDelete: boolean;
  companiesWrite: boolean;
  applicationsWrite: boolean;
  financeAccessWrite: boolean;
  /** `PUT /users/:id/roles` — allowed outside FINANCE (seniority-bounded). */
  userRolesWrite: boolean;
}

const CAPABILITIES: Record<AccessChannel, AccessCapabilities> = {
  identity: {
    roleWrite: false,
    roleDelete: false,
    companiesWrite: false,
    applicationsWrite: false,
    financeAccessWrite: true,
    userRolesWrite: true,
  },
  finance: {
    roleWrite: true,
    roleDelete: false,
    companiesWrite: true,
    applicationsWrite: true,
    financeAccessWrite: true,
    userRolesWrite: true,
  },
};

export function accessCapabilities(): AccessCapabilities {
  return CAPABILITIES[current];
}

/** A route on the current channel: which client, which path. */
export interface ChannelRoute {
  client: AxiosInstance;
  path: string;
}

const viaIdentity = (path: string): ChannelRoute => ({ client: identityApi, path });
const viaFinance = (path: string): ChannelRoute => ({ client: financeApi, path });
const pick = (identityPath: string, financePath: string): ChannelRoute =>
  current === 'finance' ? viaFinance(financePath) : viaIdentity(identityPath);

/**
 * Every access-administration route, resolved for the current channel.
 * Identity-only routes (no `/access` equivalent, no application restriction
 * in identity) go to identity from either application.
 */
export const accessRoute = {
  users: () => pick(IDENTITY.users.list, FINANCE.access.users),
  user: (userId: string) => pick(IDENTITY.users.user(userId), FINANCE.access.user(userId)),
  userRoles: (userId: string) =>
    pick(IDENTITY.users.roles(userId), FINANCE.access.userRoles(userId)),
  userCompanies: (userId: string) =>
    pick(IDENTITY.users.companies(userId), FINANCE.access.userCompanies(userId)),
  userApplications: (userId: string) =>
    pick(IDENTITY.users.applications(userId), FINANCE.access.userApplications(userId)),
  /** Finance channel only: grant one application (POST; identity has only a full PUT). */
  grantUserApplication: (userId: string) => viaFinance(FINANCE.access.userApplications(userId)),
  /** Finance channel only: revoke one application (DELETE). */
  userApplication: (userId: string, application: string) =>
    viaFinance(FINANCE.access.userApplication(userId, application)),
  /** Finance channel only. */
  applications: () => viaFinance(FINANCE.access.applications),
  roles: () => pick(IDENTITY.roles.list, FINANCE.access.roles),
  role: (roleId: string) => pick(IDENTITY.roles.role(roleId), FINANCE.access.role(roleId)),
  rolePermissions: (roleId: string) =>
    pick(IDENTITY.roles.permissions(roleId), FINANCE.access.rolePermissions(roleId)),
  assignableRoles: () => pick(IDENTITY.roles.assignable, FINANCE.access.assignableRoles),
  permissions: () => pick(IDENTITY.permissions, FINANCE.access.permissions),
  /** Login account with no employee: `/provisioning/users` | `/access/users`. */
  createUserAccount: () => pick(IDENTITY.provisioning.users, FINANCE.access.users),
  /** Login account for an existing employee. */
  employeeAccount: (employeeId: string) =>
    pick(IDENTITY.provisioning.employeeAccount(employeeId), FINANCE.access.employeeAccount(employeeId)),
  financeAccess: (employeeId: string) =>
    pick(IDENTITY.employees.financeAccess(employeeId), FINANCE.access.employeeFinanceAccess(employeeId)),
  // identity-only, from either application
  financeAccessHistory: (employeeId: string) =>
    viaIdentity(IDENTITY.employees.financeAccessHistory(employeeId)),
  employeePermissions: (employeeId: string) =>
    viaIdentity(IDENTITY.employees.permissions(employeeId)),
  resetPassword: (employeeId: string) => viaIdentity(IDENTITY.employees.resetPassword(employeeId)),
} as const;

/**
 * Bind a channel route's client and path, so a call site reads
 * `call(accessRoute.roles()).get(config)` regardless of which service serves it.
 */
export function call(route: ChannelRoute) {
  return {
    get: <T>(config?: AxiosRequestConfig) => route.client.get<T>(route.path, config),
    post: <T>(body?: unknown, config?: AxiosRequestConfig) =>
      route.client.post<T>(route.path, body, config),
    put: <T>(body?: unknown, config?: AxiosRequestConfig) =>
      route.client.put<T>(route.path, body, config),
    patch: <T>(body?: unknown, config?: AxiosRequestConfig) =>
      route.client.patch<T>(route.path, body, config),
    delete: <T>(config?: AxiosRequestConfig) => route.client.delete<T>(route.path, config),
  };
}

/** Thrown for an operation the current channel does not offer. */
export class UnsupportedAccessOperationError extends Error {
  constructor(operation: string) {
    super(`${operation} is not available in this application.`);
    this.name = 'UnsupportedAccessOperationError';
  }
}
